import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ToastService } from './toast.service';
import { ToastType, UserRole } from '../enums/enums';
import { Employee } from '../layouts/private-layout/pages/employee/services/employee.modal';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private UserRole!: number;
  public UserDetails: BehaviorSubject<Employee | null> = new BehaviorSubject<Employee | null>(null);
  public CurrentClinic: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  public IsAdminUser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private router: Router, private toast: ToastService, private datePipe: DatePipe) {
    if (this.isUserSignedIn()) {
      this.UserDetails.next(JSON.parse(this.getData('userdetails') || ''));
      this.CurrentClinic.next(this.UserDetails.value?.clinicId || 0);
    }
  }

  public setUserLoggedIn(value: boolean) {
    if (value) {
      this.router.navigate(['/home']);
    } else {
      localStorage.clear();
      this.router.navigate(['/auth']);
    }
  }

  public changeClinicId(clinicId: number) {
    this.CurrentClinic.next(clinicId);
  }

  public isUserSignedIn(): boolean {
    const token = this.getData('token');
    const tokenExpiration = this.getData('tokenExpiration');
    const userdetails = this.getData('userdetails');

    if (token && userdetails) {
      try {
        const payload = window.atob(token.split('.')[1]);
        const parsedPayload = JSON.parse(payload);

        if (tokenExpiration || 'exp' in parsedPayload) {
          const ExpTime = new Date(parseInt(parsedPayload.exp) * 1000);
          const CurrentTime = new Date();
          if (CurrentTime > ExpTime) {
            return false;
          }
        }

        if ('jti' in parsedPayload && 'exp' in parsedPayload) {
          this.setUserRole(JSON.parse(userdetails).userRoleEnum);
          this.IsAdminUser.next(this.UserRole === UserRole.ADMIN);
          return true;
        } else {
          return false;
        }
      } catch (err) {
        this.toast.showToast({
          message: 'Looks like something went wrong ! Please Try again.',
          type: ToastType.Warning
        });
        return false;
      }
    } else {
      return false;
    }
  }

  public getUserRole() {
    return this.UserRole;
  }

  public setUserRole(value: number) {
    this.UserRole = value;
  }

  /* For getting and setting Data in Local storage */

  public setData(key: string, value: any, expiredTime?: string) {
    localStorage.setItem(key, JSON.stringify(value));
    if (expiredTime) {
      this.setExpiredIn(key, expiredTime);
    }
  }

  public getData(key: string) {
    return localStorage.getItem(key);
  }

  public deleteData(key: string) {
    localStorage.removeItem(key);
  }

  public setExpiredIn(itemKey: string, expiredTime: string) {
    let specifiedTime = new Date(expiredTime);
    localStorage.setItem(`${itemKey}Expiration`, specifiedTime.toString());
  }

  validateNumber(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    // Allow backspace, delete, tab, escape, enter, and arrows
    if (
      charCode === 8 || // Backspace
      charCode === 9 || // Tab
      charCode === 27 || // Escape
      charCode === 13 || // Enter
      (charCode >= 35 && charCode <= 39) // Arrow keys
    ) {
      return true;
    }

    // Allow only digits (0-9)
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Prevent other characters
    return false;
  }

  changeStringToDate(obj: any): any {
    const updatedObj = { ...obj };
    for (const key in updatedObj) {
      if (updatedObj[key] && typeof updatedObj[key] === 'string' && this.isValidDate(updatedObj[key])) {
        updatedObj[key] = updatedObj[key] + 'T00:00:00';
      }
    }
    return updatedObj;
  }

  isValidDate(dateStr: string): boolean {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  }

  changeDateToString(obj: any): any {
    const updatedObj = { ...obj };
    Object.keys(updatedObj).forEach((key) => {
      const value = updatedObj[key];
      if (this.isDateString(value)) {
        updatedObj[key] = this.datePipe.transform(value, 'yyyy-MM-dd');
      }
    });
    return updatedObj;
  }

  isDateString(value: any): boolean {
    return typeof value === 'string' && !isNaN(Date.parse(value));
  }
}
